import {
  API_PATHS,
  type FollowInviteLanding,
  followInviteLandingSchema,
  isDesk,
  type Viewer,
} from '@alphadesk/web/contract.ts';
import { useCallback, useEffect, useState } from 'react';
import { z } from 'zod';
import { actFor, useWrite } from '../lib/act.ts';
import { useHeld } from '../lib/held.ts';
import { InviteCard } from './InviteCard.tsx';
import { Button } from './primitives/button.tsx';
import { Notice } from './primitives/shell.tsx';

export type HeldJoin = NonNullable<FollowInviteLanding['join']>;

const pickJoin = (body: FollowInviteLanding) => body.join;

/** The follow invite this browser holds, if any, and a way to let it go. */
export function useHeldJoin() {
  const {
    held: join,
    setHeld,
    reload,
  } = useHeld(API_PATHS.join, followInviteLandingSchema, pickJoin);
  const forget = useCallback(() => {
    setHeld(null);
    void fetch(API_PATHS.join, { method: 'DELETE', credentials: 'same-origin' }).catch(() => {});
  }, [setHeld]);
  return { join, forget, reload };
}

/**
 * The invite card, and on it the press that spends the invite: the server reads the code from the
 * cookie, never from here. The page reloads after, since taking it may let the viewer in and the
 * socket sends the books and names a viewer may know only as it opens. Signed out, signing in is
 * the press, so `pressable` is off.
 */
export function JoinOffer({
  held: { join, forget, reload },
  pressable = true,
  className,
}: {
  held: ReturnType<typeof useHeldJoin>;
  pressable?: boolean;
  className?: string;
}) {
  const { run, busy } = useWrite();
  const [refused, setRefused] = useState<string | null>(null);
  if (join === null) return null;

  const follow = async () => {
    setRefused(null);
    const answer = await run(() =>
      actFor({ do: 'accept-follow-invite' }, z.object({ href: z.string() })),
    );
    if (answer.ok) {
      window.location.assign(answer.value.href);
      return;
    }
    setRefused(answer.violations.join(' '));
    reload();
  };

  return (
    <InviteCard
      picture={join.picture}
      name={join.name}
      title={
        join.open
          ? `${join.name} invites you to follow them`
          : `${join.name}’s invite is no longer open`
      }
      hint={
        join.open
          ? `It is for ${join.email} alone, and works only signed in with Google as that address.${join.bio ? ` ${join.bio}` : ''}`
          : `It lets nobody in now. Ask ${join.name} for a new link of your own.`
      }
      className={className}
    >
      <div className="flex flex-wrap gap-2">
        {pressable && join.open && (
          <Button tone="primary" busy={busy} onClick={() => void follow()}>
            Follow {join.name}
          </Button>
        )}
        <Button onClick={forget}>{join.open ? 'Not now' : 'Dismiss'}</Button>
      </div>
      {refused && <Notice>{refused}</Notice>}
    </InviteCard>
  );
}

/**
 * Inside the console: a link to somebody the viewer already follows, or their own, is let go, as is
 * any link an admin holds, since admins follow nobody.
 */
export function JoinPrompt({ viewer }: { viewer: Viewer }) {
  const held = useHeldJoin();
  const { join, forget } = held;
  const theirs =
    join !== null &&
    (isDesk(viewer) ||
      viewer.follows.includes(join.analystId) ||
      viewer.analyst?.id === join.analystId);
  useEffect(() => {
    if (theirs) forget();
  }, [theirs, forget]);
  return theirs ? null : <JoinOffer held={held} className="mb-6" />;
}
